import { gql } from "@apollo/client";
import { createContext, useCallback, useContext } from "react";
import useUser from "@src/features/app/hooks/useUser";
import { useChatSidebar } from "@src/features/aiAssist/hooks/useChatSidebar";
import { AiChatType, Ff } from "@src/types.generated";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { useToast } from "@src/lib/hooks/useToast";
import type { AgentPanelGates } from "@src/features/app/chatSurface";
import {
  OpusAgentFieldsFragment,
  useOpusAgentsQuery,
  useSetOpusAgentArchivedMutation,
} from "./agentPanel.generated";
import { useOpusAgentDrawerStore } from "./useOpusAgentDrawerStore";
import { findManagerAgent } from "./managerAgents";

export const ASK_OPUS_AGENT_ID = "ask-opus";

const AgentPanelGatesContext = createContext<AgentPanelGates>({
  askOpus: false,
  agents: false,
  managerAgents: false,
});
export const AgentPanelGatesProvider = AgentPanelGatesContext.Provider;
export const useAgentPanelGates = (): AgentPanelGates =>
  useContext(AgentPanelGatesContext);

// What the panel shows: the stored pick while it is still available, else
// Ask Opus, else the first active agent. A stored agent is kept while the
// list loads so a reload doesn't flash Ask Opus first. A manager agent is
// available while its gate is on.
export const resolvePanelAgentId = ({
  storedAgentId,
  gates,
  agents,
  agentsLoaded,
}: {
  storedAgentId: string | null;
  gates: AgentPanelGates;
  agents: readonly Pick<OpusAgentFieldsFragment, "id" | "archivedAt">[];
  agentsLoaded: boolean;
}): string | null => {
  if (findManagerAgent(storedAgentId))
    return gates.managerAgents
      ? storedAgentId
      : gates.askOpus
        ? ASK_OPUS_AGENT_ID
        : null;
  if (
    storedAgentId &&
    storedAgentId !== ASK_OPUS_AGENT_ID &&
    gates.agents &&
    (!agentsLoaded || agents.some((agent) => agent.id === storedAgentId))
  )
    return storedAgentId;
  if (gates.askOpus) return ASK_OPUS_AGENT_ID;
  if (!gates.agents) return null;
  return agents.find((agent) => !agent.archivedAt)?.id ?? null;
};

export const selectPanelAgent = (
  agentId: string,
  userId: string | null,
): void => {
  if (userId)
    useOpusAgentDrawerStore.getState().setSelectedAgent(agentId, userId);
};

export const useAgentPanel = (): {
  gates: AgentPanelGates;
  isOpen: boolean;
  selectedAgentId: string | null;
  selectedAgent: OpusAgentFieldsFragment | null;
  agents: OpusAgentFieldsFragment[];
  archivedAgents: OpusAgentFieldsFragment[];
  agentsLoading: boolean;
  userId: string | null;
  openAgent: (agentId: string) => void;
  toggleAgent: (agentId: string) => void;
} => {
  const gates = useAgentPanelGates();
  const { user } = useUser();
  const userId = user?.id != null ? String(user.id) : null;
  const storedAgentId = useOpusAgentDrawerStore((state) =>
    userId && state.userId === userId ? state.selectedAgentId : null,
  );
  const killSwitchEnabled = useFeatureFlag(Ff.OpusAgentKillSwitch);
  const { data, loading, error } = useOpusAgentsQuery({
    skip: !gates.agents || killSwitchEnabled,
  });
  const allAgents = gates.agents ? data?.OpusAgents ?? [] : [];
  const agentsLoaded =
    killSwitchEnabled ||
    (!loading && (data !== undefined || error !== undefined));
  const selectedAgentId = resolvePanelAgentId({
    storedAgentId,
    gates,
    agents: allAgents,
    agentsLoaded,
  });
  const { isOpen, open, close } = useChatSidebar(AiChatType.KnowledgeBase);

  const openAgent = useCallback(
    (agentId: string) => {
      selectPanelAgent(agentId, userId);
      open();
    },
    [open, userId],
  );
  const toggleAgent = useCallback(
    (agentId: string) => {
      if (isOpen && agentId === selectedAgentId) close();
      else openAgent(agentId);
    },
    [isOpen, selectedAgentId, close, openAgent],
  );

  return {
    gates,
    userId,
    isOpen,
    selectedAgentId,
    selectedAgent:
      allAgents.find((agent) => agent.id === selectedAgentId) ?? null,
    agents: allAgents.filter((agent) => !agent.archivedAt),
    archivedAgents: allAgents.filter((agent) => agent.archivedAt),
    agentsLoading: gates.agents && !agentsLoaded,
    openAgent,
    toggleAgent,
  };
};

export const useSetAgentArchived = (): {
  setArchived: (
    agent: Pick<OpusAgentFieldsFragment, "id" | "name">,
    archived: boolean,
  ) => Promise<boolean>;
  loading: boolean;
} => {
  const [setOpusAgentArchived, { loading }] = useSetOpusAgentArchivedMutation();
  const { addToast, addErrorToast } = useToast();
  const setArchived = useCallback(
    async (
      agent: Pick<OpusAgentFieldsFragment, "id" | "name">,
      archived: boolean,
    ) => {
      const ok = await setOpusAgentArchived({
        variables: { id: agent.id, archived },
      })
        .then(({ data }) => Boolean(data?.setOpusAgentArchived?.success))
        .catch(() => false);
      if (ok)
        addToast({
          message: archived
            ? `Archived ${agent.name}`
            : `Unarchived ${agent.name}`,
        });
      else
        addErrorToast({
          message: archived
            ? "Could not archive this agent"
            : "Could not unarchive this agent",
          callsite: "agent_panel_set_archived",
        });
      return ok;
    },
    [setOpusAgentArchived, addToast, addErrorToast],
  );
  return { setArchived, loading };
};

gql`
  fragment OpusAgentFields on OpusAgent {
    id
    createdAt
    name
    emoji
    instructions
    suggestedQuestions
    archivedAt
    avatarKind
    avatarBotShape
    avatarBotColor
    avatarImageUrl
    template {
      id
      key
      description
    }
    schedule {
      rrule
      tzid
      prompt
      enabled
      nextFireAt
      summary
      runsAsViewer
    }
  }

  query OpusAgents {
    OpusAgents {
      ...OpusAgentFields
    }
  }

  mutation SetOpusAgentArchived($id: UUID!, $archived: Boolean!) {
    setOpusAgentArchived(id: $id, archived: $archived) {
      success
      error {
        code
        message
      }
      agent {
        ...OpusAgentFields
      }
    }
  }
`;
